import { useRef } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { calcChartData } from '@aytm/app/stats/page/question-views/psc/lib/utils'
import { calcTableProps } from '@aytm/app/stats/page/question-views/psc/lib/table-utils'

import usePolarityScaleState, {
  calcChartHeight
} from '@aytm/app/stats/page/question-views/psc/states/usePolarityScaleState'

// components
import ChartPlaceholder from '@aytm/app/stats/page/question-views/components/charts/placeholders/Chart'

import Controls from '@aytm/app/stats/page/question-views/psc/components/Controls'
import Chart from '@aytm/app/stats/page/question-views/psc/components/Chart'
import Table from '@aytm/app/stats/components/data-views/table/components/Table'

import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

import { isComponentOn } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { resolvePercentDecimals } from '@aytm/app/stats/page/components/lib/utils'
import { isNoneChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'

const Block = ({
  id,
  params,
  paramsRevision,
  format,
  link,
  answer,
  batches,
  degree,
  palette,
  reportPercentDecimals,
  components,
  questionChartMode,
  globalChartMode,
  isAnimated,
  isSurveyGuest,
  isReportXpert,
  onReady,
  onStateChange,
  onChartExport
}) => {
  const apiRef = useRef({})

  const { mode, neutral, onModelChange } = usePolarityScaleState({
    params,
    paramsRevision,
    format,
    batches,
    chartApiRef: apiRef,
    onReady,
    onStateChange
  })

  const chartData = calcChartData({ answer, batches, degree, neutral })
  const percentDecimals = resolvePercentDecimals(format, reportPercentDecimals) ?? 1
  const tableProps = calcTableProps({ mode, answer, chartData, percentDecimals, onModelChange })

  return (
    <BlockWrapper {...{ id, link, answer, isSurveyGuest, isReportXpert }}>
      {isComponentOn(components, 'controls') && (
        <Controls mode={mode} neutral={neutral} degree={degree} onModelChange={onModelChange} />
      )}
      {isComponentOn(components, 'chart') && !isNoneChartMode(questionChartMode, globalChartMode) ? (
        <ChartPlaceholder
          height={calcChartHeight(batches)}
          required
          isLoading={'loading' == answer}
          isAnimated={isAnimated}
        >
          <Chart
            id={id}
            mode={mode}
            batches={batches}
            palette={palette}
            chartData={chartData}
            percentDecimals={percentDecimals}
            onExport={onChartExport}
            apiRef={apiRef}
          />
        </ChartPlaceholder>
      ) : null}
      {isComponentOn(components, 'table') && <Table id={id} {...tableProps} />}
    </BlockWrapper>
  )
}

Block.displayName = 'Block'

Block.propTypes = {
  id: PropTypes.string,
  params: PropTypes.shape({ psc: PropTypes.string }),
  paramsRevision: PropTypes.number,
  format: PropTypes.object,
  link: PropTypes.string,
  answer: answerPropType,
  batches: PropTypes.array,
  degree: PropTypes.number,
  palette: PropTypes.array,
  reportPercentDecimals: PropTypes.number,
  questionChartMode: PropTypes.string,
  globalChartMode: PropTypes.string,
  components: PropTypes.object,
  isAnimated: PropTypes.bool,
  isReportXpert: PropTypes.bool,
  isSurveyGuest: PropTypes.bool,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func,
  onChartExport: PropTypes.func
}

export default Block
